<template>
  <!--
    ArtifactDetail SECTION — Metric artifact, "Display" tab.

    WHAT: Pure presentation config for how the metric renders on /insight — no DAs,
      no SQL. Structured editor over record.display_config.

    CONTRACT (props { record, artifact, context }):
      READS  record.display_config.{format, decimal_places, higher_is_better,
             show_efctv, show_all_observations, thresholds[]}.
      EMITS  patch({ display_config }) — merges the edited keys back over the
             existing display_config. show_all_observations is written only when
             the toggle is on (else the key is deleted); thresholds are the
             traffic-light breakpoints (interpreted with higher_is_better).

    NOTES:
      - hydrating guard: set false on $nextTick after mount; emit() returns early
        while hydrating (fields bind to a local cfg copy, saved on @change/@input).
      - NOT selfChrome — rendered inside ArtifactDetail's standard titled card.
  -->
  <div class="space-y-4">
    <div class="grid grid-cols-2 gap-4">
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Format</label>
        <select v-model="cfg.format" class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400" @change="emit">
          <option value="percentage">percentage (0.69 → 69%)</option>
          <option value="count">count (#)</option>
          <option value="decimal">decimal (1.83)</option>
          <option value="currency">currency ($)</option>
          <option value="per_1k">per 1,000</option>
        </select>
      </div>
      <div>
        <label class="block text-[11px] font-medium text-gray-500 mb-1">Decimal places</label>
        <input
v-model.number="cfg.decimal_places" type="number" min="0" max="4"
          class="w-full text-xs px-2 py-1.5 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400" @input="emit" />
      </div>
    </div>

    <div class="flex items-center gap-6">
      <label class="flex items-center gap-2 text-xs text-gray-700">
        <input v-model="cfg.higher_is_better" type="checkbox" class="rounded" @change="emit" />
        Higher is better
      </label>
      <label class="flex items-center gap-2 text-xs text-gray-700">
        <input v-model="cfg.show_efctv" type="checkbox" class="rounded" @change="emit" />
        Show "no runout" (EFCTV) marker
      </label>
      <label class="flex items-center gap-2 text-xs text-gray-700">
        <input v-model="showAllObs" type="checkbox" class="rounded" @change="emit" />
        Show all observations (no year filter)
      </label>
    </div>

    <div>
      <div class="flex items-center justify-between mb-1.5">
        <label class="text-[11px] font-medium text-gray-500">Thresholds (traffic-light breakpoints)</label>
        <button class="font-mono text-[11px] text-blue-600 hover:text-blue-700" @click="addThreshold">＋ add</button>
      </div>
      <div class="flex flex-wrap items-center gap-1.5">
        <span
v-for="(t, i) in thresholds" :key="i"
          class="inline-flex items-center gap-1 font-mono text-[11.5px] text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-2 py-1">
          <input
v-model.number="thresholds[i]" type="number" step="0.01"
            class="w-14 bg-transparent focus:outline-none text-blue-800" @input="emit" />
          <button class="text-blue-400 hover:text-red-500 leading-none" @click="removeThreshold(i)">×</button>
        </span>
        <span v-if="!thresholds.length" class="text-[11px] text-gray-400">None — flat display.</span>
      </div>
      <p class="text-[10px] text-gray-400 mt-1.5">Rates below the first breakpoint show red, between show amber, above show green (respecting "higher is better").</p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DisplaySection',
  props: {
    record: { type: Object, required: true },
    artifact: { type: Object, required: true },
    context: { type: Object, default: () => ({}) },
  },
  emits: ['patch'],
  data() {
    const dc = this.record.display_config || {};
    return {
      cfg: {
        format: dc.format || 'percentage',
        decimal_places: dc.decimal_places ?? 1,
        higher_is_better: dc.higher_is_better !== false,
        show_efctv: !!dc.show_efctv,
      },
      showAllObs: !!dc.show_all_observations,
      thresholds: Array.isArray(dc.thresholds) ? [...dc.thresholds] : [],
      hydrating: true,
    };
  },
  mounted() { this.$nextTick(() => { this.hydrating = false; }); },
  methods: {
    addThreshold() { this.thresholds.push(0); this.emit(); },
    removeThreshold(i) { this.thresholds.splice(i, 1); this.emit(); },
    emit() {
      if (this.hydrating) return;
      const dc = {
        ...(this.record.display_config || {}),
        format: this.cfg.format,
        decimal_places: this.cfg.decimal_places,
        higher_is_better: this.cfg.higher_is_better,
        show_efctv: this.cfg.show_efctv,
        thresholds: this.thresholds.filter((t) => t !== null && t !== undefined),
      };
      if (this.showAllObs) dc.show_all_observations = true; else delete dc.show_all_observations;
      this.$emit('patch', { display_config: dc });
    },
  },
};
</script>
